import { MenuItem, Stack } from '@mui/material';
import { Button, colors, IconButton, Typography } from '@sweep-io/sweep-design';
import { Close, Info } from '@sweep-io/sweep-design/dist/icons';
import { createElement, memo, useCallback, useState } from 'react';
import { Prompt, PromptGroup_deprecated, PromptGroup } from '@server/prompts';
import SweepTextField from '../../common/SweepTextField';
import SweepSelect from '../../common/SweepSelect';
import { usePrompt } from './usePrompt';
import { AgentChatInstanceProvider } from '../AgentChatInstanceContext';
import { ChatInstance } from '../ChatInstance';
import { AgentChat } from '../AgentChat';
import useAgentsChats from '../useAgentsChats';
import { useRunOnceWhenTruthy } from '../../common/useRunOnceWhenTruthy';
import { CopyToClipboardButton } from './copy-to-clipboard/CopyToClipboardButton';
import { UsePromptInChatButton } from './UsePromptInChatButton';
import { getTextContent } from '../../common/ai-chat/aiChatHelpers';
import useConfirm from '../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { ConfirmLeaveWithoutSaveDialog } from '../../common/dialogs/ConfirmLeaveWithoutSave/ConfirmLeaveWithoutSaveDialog';
import { BuildDefinitionProvider } from '../../common/build-definition/useBuildDefinitions';

interface PromptFormData {
  name: string;
  promptText: string;
  categoryId: string | null;
}

interface PromptEditScreenProps {
  categories: (PromptGroup_deprecated | PromptGroup)[];
  initialCategoryId: string | null;
  promptToEdit: Prompt | null;
  onClose: () => void;
  onSave: (
    promptData: { name: string; promptText: string; promptGroupId?: string },
    promptId?: string,
  ) => Promise<void>;
}

const PromptEditScreenContent = memo(
  ({ categories, initialCategoryId, promptToEdit, onClose, onSave }: PromptEditScreenProps) => {
    const isEditMode = !!promptToEdit;

    const [formData, setFormData] = useState<PromptFormData>(() => ({
      name: promptToEdit?.name ?? '',
      promptText: promptToEdit?.promptText ?? '',
      categoryId: promptToEdit?.promptGroupId ?? initialCategoryId,
    }));

    const [isOptimizing, setIsOptimizing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const { optimizePrompt } = usePrompt();
    const { activeAgent, activeChat, onConfirmMessage, createGhostChat } = useAgentsChats();
    const { openConfirm } = useConfirm();
    const isChatOnGoing = activeChat?.data?.aiChatDetails?.messages?.find(
      (message) => message.role === 'assistant',
    );
    const userMessage = getTextContent(
      activeChat?.data?.aiChatDetails?.messages?.[0]?.content ?? '',
    );

    useRunOnceWhenTruthy(() => {
      if (!activeAgent) return;
      createGhostChat({ agentId: activeAgent?.id, agentType: activeAgent?.type });
    }, !!activeAgent);

    const updateFormData = useCallback((updates: Partial<PromptFormData>) => {
      setFormData((prev) => ({ ...prev, ...updates }));
    }, []);

    const handleOptimize = useCallback(async () => {
      if (!formData.promptText) return;
      setIsOptimizing(true);
      const optimizedPrompt = await optimizePrompt(formData.promptText);
      if (optimizedPrompt) {
        updateFormData({ promptText: optimizedPrompt });
      }
      setIsOptimizing(false);
    }, [formData.promptText, optimizePrompt, updateFormData]);

    const handleRunTest = useCallback(() => {
      if (!activeAgent || !formData.promptText) return;
      onConfirmMessage({
        messageStr: formData.promptText,
        agentId: activeAgent.id,
        agentType: activeAgent.type,
        isFirstMessage: true,
        disableNavigation: true,
      });
    }, [activeAgent, formData.promptText, onConfirmMessage]);

    const handleSave = useCallback(async () => {
      if (!formData.name.trim() || !formData.promptText.trim()) return;

      setIsSaving(true);
      try {
        await onSave(
          {
            name: formData.name,
            promptText: formData.promptText,
            promptGroupId: formData.categoryId ?? undefined,
          },
          promptToEdit?.id,
        );
        onClose();
      } finally {
        setIsSaving(false);
      }
    }, [formData, onSave, onClose, promptToEdit]);

    const canSave = formData.name.trim() && formData.promptText.trim();
    const hasChanges = isEditMode
      ? formData.name !== promptToEdit?.name ||
        formData.promptText !== promptToEdit?.promptText ||
        formData.categoryId !== promptToEdit?.promptGroupId
      : formData.name.trim() !== '' || formData.promptText.trim() !== '';

    const handleCloseAttempt = useCallback(async () => {
      if (hasChanges) {
        const isConfirmed = await openConfirm(createElement(ConfirmLeaveWithoutSaveDialog));
        if (!isConfirmed) return;
      }
      onClose();
    }, [hasChanges, openConfirm, onClose]);

    return (
      <Stack direction="row" sx={{ height: '100%', overflow: 'hidden' }}>
        <Stack
          sx={{
            width: '50%',
            borderRight: `1px solid ${colors.grey[300]}`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 4, pb: 3 }}
          >
            <Typography variant="h3-medium">{isEditMode ? 'Edit Prompt' : 'New Prompt'}</Typography>
          </Stack>

          <Stack sx={{ flex: 1, p: 4, pt: 0, gap: 2, overflow: 'auto' }}>
            <SweepTextField
              value={formData.name}
              onChange={(e) => updateFormData({ name: e?.target.value ?? '' })}
              disableConfirmPropagation
              TextFieldProps={{
                placeholder: 'Add title',
              }}
              autoFocus
            />

            <SweepSelect
              SelectProps={{
                value: formData.categoryId ?? '',
                onChange: (e) =>
                  updateFormData({
                    categoryId: e.target.value === '' ? null : (e.target.value as string),
                  }),
                placeholder: 'Select Category (Optional)',
                displayEmpty: true,
              }}
              renderValue={(value) => {
                if (!value) {
                  return (
                    <Stack
                      direction="row"
                      gap={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="body" color={colors.grey[700]}>
                        Select Category
                      </Typography>

                      <Typography variant="body" color={colors.grey[500]}>
                        *Optional
                      </Typography>
                    </Stack>
                  );
                }

                const category = categories.find((category) => category.id === value);
                return <>{category?.name}</>;
              }}
              FormControlProps={{ fullWidth: true }}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </SweepSelect>

            <Stack sx={{ flex: 1, minHeight: '200px', position: 'relative' }}>
              <Stack position="absolute" bottom={16} right={16} zIndex={1}>
                <Button
                  variant="flat-blue"
                  size="small"
                  startIconName="EditAI"
                  disabled={!formData.promptText}
                  loading={isOptimizing}
                  onClick={handleOptimize}
                >
                  Optimize
                </Button>
              </Stack>

              <SweepTextField
                label="Add Prompt"
                value={formData.promptText}
                onChange={(e) => updateFormData({ promptText: e?.target.value ?? '' })}
                disableConfirmPropagation
                autoFocus={false}
                FormControlProps={{
                  sx: {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  },
                }}
                TextFieldProps={{
                  multiline: true,
                  placeholder:
                    'e.g. For a specific Bundle, list its Options and Configuration Attributes. Useful for scoping complexity ahead of migration.',
                  sx: {
                    flex: 1,
                    '& .MuiInputBase-root': {
                      height: '100%',
                      alignItems: 'flex-start',
                    },
                    '& textarea': {
                      height: '100% !important',
                      resize: 'none',
                      overflow: 'auto !important',
                      minHeight: '150px !important',
                    },
                  },
                }}
              />
            </Stack>
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            sx={{
              p: 4,
            }}
          >
            <Stack direction="row" gap={2} alignItems="center">
              <CopyToClipboardButton textToCopy={formData.promptText} size="small" />
              <UsePromptInChatButton
                promptText={formData.promptText}
                size="small"
                disabled={!formData.promptText.trim() || !promptToEdit?.id || hasChanges}
                onSuccess={onClose}
              />
            </Stack>
            <Button onClick={handleSave} disabled={!canSave || !hasChanges} loading={isSaving}>
              Save
            </Button>
          </Stack>
        </Stack>

        <Stack sx={{ width: '50%', display: 'flex', flexDirection: 'column', p: 4, pb: 3 }}>
          <Stack gap={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h3-medium">Test Prompt</Typography>
              <IconButton variant="flat" size="small" onClick={handleCloseAttempt}>
                <Close />
              </IconButton>
            </Stack>

            <Stack
              p="8px 16px"
              sx={{ backgroundColor: colors.grey[100], borderRadius: '8px', gap: 2 }}
              direction="row"
              alignItems="center"
            >
              <Stack>
                <Info size="medium" />
              </Stack>
              <Typography variant="body">
                The conversation you make here while testing will be saved as any other conversation
                on the chat
              </Typography>
            </Stack>

            <Stack sx={{ alignSelf: 'flex-start', pb: 2 }}>
              <Button
                size="medium"
                startIconName="Play"
                variant="secondary"
                onClick={handleRunTest}
                disabled={
                  !formData.promptText || !!(userMessage && userMessage === formData.promptText)
                }
              >
                Run Test
              </Button>
            </Stack>
          </Stack>

          <Stack sx={{ flex: 1, overflow: 'hidden' }}>
            <Stack
              sx={{
                flex: 1,
                minHeight: 0,
                height: '100%',
                '& .agent-chat-input-wrapper': { p: 0, pl: 1 },
              }}
            >
              <AgentChat
                noteAlignment="left"
                hideFirstMessage
                hideOverlayTop
                hideAgentNameButton
                scrollAreaPadding={0}
                hideAgentInfo
                disableInputModeSelector
                disableInput={!isChatOnGoing || !formData.promptText}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    );
  },
);

export const PromptEditScreen = (props: PromptEditScreenProps) => {
  return (
    <BuildDefinitionProvider>
      <AgentChatInstanceProvider instanceId={ChatInstance.editPrompt}>
        <PromptEditScreenContent {...props} />
      </AgentChatInstanceProvider>
    </BuildDefinitionProvider>
  );
};
